"use client"

import { useContext } from "react"
import { SidebarContext } from "../SidebarContext"
import type { SidebarContextValue } from "../Sidebar.types"

/**
 * Hook para acceder al contexto del Sidebar
 *
 * @description
 * Hook personalizado que provee acceso al contexto compartido del Sidebar.
 * Debe ser utilizado dentro de un componente que esté envuelto por
 * `<Sidebar>` o `<SidebarProvider>`.
 *
 * Lanza un error si se utiliza fuera del contexto apropiado, ayudando
 * a detectar problemas de composición durante el desarrollo.
 *
 * @throws {Error} Si se usa fuera de un SidebarProvider
 *
 * @returns {SidebarContextValue} El valor del contexto del sidebar
 *
 * @example
 * ```tsx
 * function CustomToggle() {
 *   const { open, setOpen } = useSidebarContext()
 *
 *   return (
 *     <button onClick={() => setOpen(!open)}>
 *       {open ? 'Cerrar' : 'Abrir'}
 *     </button>
 *   )
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Acceder a configuración
 * function CustomItem() {
 *   const { colors, dimensions } = useSidebarContext()
 *
 *   return (
 *     <div style={{
 *       background: colors.hover,
 *       width: dimensions.expandedWidth
 *     }}>
 *       Item
 *     </div>
 *   )
 * }
 * ```
 */
export function useSidebarContext(): SidebarContextValue {
  const context = useContext(SidebarContext)

  if (!context) {
    throw new Error(
      "useSidebarContext debe ser utilizado dentro de un componente <Sidebar> o <SidebarProvider>. " +
        "Asegúrate de que tu componente esté envuelto correctamente en la jerarquía del Sidebar.",
    )
  }

  return context
}
